import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  SALES_INVENTORY_BORROWERS_TTL,
  SalesInventoryBorrowersStateModel,
  borrowerDrillDownKey,
} from 'ng2-app/models/sales-inventory/sales-inventory-state.model';
import {
  BorrowerDrillDownPage,
  BorrowerDrillDownQuery,
} from 'ng2-app/models/sales-inventory/sales-inventory.model';
import { SalesInventoryService } from 'ng2-app/sales-inventory/sales-inventory.service';
import {
  LoadBorrowerDrillDown,
  LoadMoreBorrowerDrillDown,
} from 'ng2-app/store/sales-inventory/sales-inventory.actions';

@State<SalesInventoryBorrowersStateModel>({
  name: 'salesInventoryBorrowers',
  defaults: {
    borrowers: {},
    page: {},
    loadingMore: {},
    loading: false,
    fetching: false,
    lastSync: {},
  },
})
@Injectable()
export class SalesInventoryBorrowersState {
  @Selector()
  static loading(state: SalesInventoryBorrowersStateModel) {
    return state?.loading ?? null;
  }

  @Selector()
  static fetching(state: SalesInventoryBorrowersStateModel) {
    return state?.fetching ?? null;
  }

  /** Full keyed map (key = borrowerDrillDownKey). Prefer `borrowersFor` at call sites. */
  @Selector()
  static borrowers(state: SalesInventoryBorrowersStateModel) {
    return state?.borrowers ?? {};
  }

  /** Per-key pagination { nextCursor, hasMore }, keyed by borrowerDrillDownKey. */
  @Selector()
  static pages(state: SalesInventoryBorrowersStateModel) {
    return state?.page ?? {};
  }

  /** Per-key "a load-more (append) is in flight" flags, keyed by borrowerDrillDownKey. */
  @Selector()
  static loadingMore(state: SalesInventoryBorrowersStateModel) {
    return state?.loadingMore ?? {};
  }

  /** Rows for a specific drill-down scope (lender user + query), or [] if not loaded. */
  static borrowersFor(lenderUserId: number, query: BorrowerDrillDownQuery) {
    const key = borrowerDrillDownKey(lenderUserId, query);
    return createSelector(
      [SalesInventoryBorrowersState],
      (state: SalesInventoryBorrowersStateModel) => state.borrowers[key] ?? []
    );
  }

  constructor(private _salesInventoryService: SalesInventoryService) {}

  private isCacheValid(
    state: SalesInventoryBorrowersStateModel,
    key: string,
    force: boolean
  ): boolean {
    if (force) {
      return false;
    }
    const cache = state.lastSync[key];
    if (!cache || !(cache instanceof Date)) {
      return false;
    }
    return cache.getTime() + SALES_INVENTORY_BORROWERS_TTL > Date.now();
  }

  @Action(LoadBorrowerDrillDown)
  loadBorrowerDrillDown(
    ctx: StateContext<SalesInventoryBorrowersStateModel>,
    { lenderUserId, query, overrideCache = false }: LoadBorrowerDrillDown
  ): Observable<BorrowerDrillDownPage> {
    const key = borrowerDrillDownKey(lenderUserId, query);
    const state = ctx.getState();

    // Cache is per (lenderUserId + query): a different view/filter refetches.
    if (this.isCacheValid(state, key, overrideCache)) {
      return of({
        rows: state.borrowers[key],
        nextCursor: state.page[key]?.nextCursor ?? null,
        hasMore: state.page[key]?.hasMore ?? false,
      });
    }

    const hasData = !!state.borrowers[key];
    ctx.patchState({ fetching: true, loading: !hasData });

    // First page: replaces any prior rows/cursor for this scope.
    return this._salesInventoryService.getBorrowerDrillDown(lenderUserId, query).pipe(
      tap((page) => {
        const current = ctx.getState();
        ctx.patchState({
          borrowers: { ...current.borrowers, [key]: page.rows },
          page: { ...current.page, [key]: { nextCursor: page.nextCursor, hasMore: page.hasMore } },
          lastSync: { ...current.lastSync, [key]: new Date() },
          loading: false,
          fetching: false,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, fetching: false });
        return throwError(() => error);
      })
    );
  }

  @Action(LoadMoreBorrowerDrillDown)
  loadMoreBorrowerDrillDown(
    ctx: StateContext<SalesInventoryBorrowersStateModel>,
    { lenderUserId, query }: LoadMoreBorrowerDrillDown
  ): Observable<BorrowerDrillDownPage> {
    const key = borrowerDrillDownKey(lenderUserId, query);
    const state = ctx.getState();
    const cursor = state.page[key]?.nextCursor ?? null;

    // Nothing more to load, or a load-more is already in flight → no-op.
    if (!cursor || !state.page[key]?.hasMore || state.loadingMore[key]) {
      return of({
        rows: state.borrowers[key] ?? [],
        nextCursor: cursor,
        hasMore: state.page[key]?.hasMore ?? false,
      });
    }

    ctx.patchState({ loadingMore: { ...state.loadingMore, [key]: true } });

    // Next page: keyset cursor guarantees no overlap, so we append.
    return this._salesInventoryService.getBorrowerDrillDown(lenderUserId, query, cursor).pipe(
      tap((page) => {
        const current = ctx.getState();
        ctx.patchState({
          borrowers: {
            ...current.borrowers,
            [key]: [...(current.borrowers[key] ?? []), ...page.rows],
          },
          page: { ...current.page, [key]: { nextCursor: page.nextCursor, hasMore: page.hasMore } },
          lastSync: { ...current.lastSync, [key]: new Date() },
          loadingMore: { ...current.loadingMore, [key]: false },
        });
      }),
      catchError((error) => {
        const current = ctx.getState();
        ctx.patchState({ loadingMore: { ...current.loadingMore, [key]: false } });
        return throwError(() => error);
      })
    );
  }
}
